/**
 * An AI render started by an agent: the camera's own flow without the camera UI — a shot of
 * the live 3D view, the render service, a gallery record that updates in place.
 */
import { aiRenderIsTest, buildPayload, deviceId, renderSize, RENDER_STYLES, RenderTimeout, submitRender, waitForRender, type RenderType } from '../state/aiRender';
import { gallery, newGalleryId } from '../state/gallery';
import { store } from '../state/store';
import type { EditorAccess } from './editorAccess';
import type { RenderOutcome, RenderRequestInput } from './toolRuntime';
import { language, t } from '../i18n/I18n';

const SNAPSHOT_MAX_SIDE = 1600;
const nextFrame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

async function imageSize(blob: Blob): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return size;
  }
  return { width: 0, height: 0 };
}

export async function renderThroughService(input: RenderRequestInput, editor: EditorAccess, signal?: AbortSignal, onProgress?: (elapsedMs: number) => void): Promise<RenderOutcome> {
  const state = store.get();
  if (!state.scene) throw new Error('No project is open.');
  if (input.type === 'redesign' && input.style && !RENDER_STYLES.some(style => style.id === input.style)) throw new Error(`Unknown style "${input.style}".`);
  if (state.mode === 'plan') {
    // The service renders a picture of the 3D view; give the scene a couple of frames to draw.
    store.set({ mode: 'scene', showCatalog: false, showGallery: false, cameraMode: false });
    await nextFrame(); await nextFrame(); await new Promise<void>(resolve => setTimeout(resolve, 250));
  }
  const jpeg = await editor.scene3d()?.snapshot(SNAPSHOT_MAX_SIDE);
  if (!jpeg) throw new Error('Could not take a picture of the 3D view.');
  const { width, height } = await imageSize(jpeg);
  const renderType: RenderType = input.type === 'photo' ? 'renderai' : 'airender';
  const style = input.type === 'redesign' ? input.style ?? RENDER_STYLES[0].id : undefined;
  const id = newGalleryId('render');
  const started = Date.now();
  await gallery.add({ id, projectId: state.projectId, kind: 'render', createdAt: started, status: 'running', type: renderType, style, prompt: input.prompt, width, height });
  const transport = { device: deviceId(), test: aiRenderIsTest() };
  try {
    const payload = buildPayload({ type: renderType, prompt: input.prompt, style, roomType: state.scene.roomType });
    const size = renderSize(input.quality ?? 'FullHD', { width, height });
    const { id: renderId } = await submitRender({ type: renderType, payload, jpeg, title: state.projectName || 'Home Planner Web', locale: language(), size }, transport);
    console.info('[ai-render] sent by an agent', renderType, renderId);
    await gallery.update(id, { renderId });
    const result = await waitForRender(renderId, { ...transport, onTick: onProgress, sleep: ms => new Promise<void>((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      signal?.addEventListener('abort', () => { clearTimeout(timer); reject(Object.assign(new Error(t('#AgentStopped')), { name: 'AbortError' })); }, { once: true });
    }) });
    await gallery.update(id, { status: 'done', imageUrl: result.imageUrl, thumbUrl: result.thumbUrl, initImageUrl: result.initImageUrl, width: result.width ?? width, height: result.height ?? height });
    return { imageUrl: result.imageUrl, galleryId: id, elapsedMs: Date.now() - started };
  } catch (error) {
    if (error instanceof RenderTimeout) throw new Error('The server is still rendering; the picture will appear in the gallery when it is done.');
    if (error instanceof Error && error.name === 'AbortError') throw error;
    const message = error instanceof Error ? error.message : String(error);
    await gallery.update(id, { status: 'error', error: message });
    throw new Error(message);
  }
}
